import { Injectable, inject } from '@angular/core';
import {
  AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser';
import { resolveApiBaseUrl } from '~/core/config/api-base-url';
import { RoleService } from '~/core/services/role.service';

interface FrontendEnv {
  COOASS_BACKEND_SCOPE?: string;
  tenantId?: string;
  clientId?: string;
  environment?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly roleService = inject(RoleService);
  private readonly apiBaseUrl = resolveApiBaseUrl();
  private readonly env = this.getEnv();
  private readonly msal = this.createMsalClient();
  private initializePromise: Promise<void> | null = null;

  private get frontendClientId(): string {
    return (this.env.clientId || '').trim();
  }

  private get tenantId(): string {
    return (this.env.tenantId || '').trim();
  }

  private get backendScope(): string {
    return (this.env.COOASS_BACKEND_SCOPE || '').trim();
  }

  private get scopes(): string[] {
    const clientId = this.frontendClientId;
    const backendScope = this.backendScope;
    return backendScope ? [backendScope] : clientId ? [`api://${clientId}/.default`] : ['openid', 'profile', 'email'];
  }

  constructor() {
    console.log('[AUTH] AuthService initialized', {
      hasTenantId: !!this.tenantId,
      hasClientId: !!this.frontendClientId,
      hasBackendScope: !!this.backendScope,
      apiBaseUrl: this.apiBaseUrl,
      scopes: this.scopes,
    });
  }

  async initialize(): Promise<void> {
    if (!this.initializePromise) {
      this.initializePromise = this.initializeInternal();
    }
    return this.initializePromise;
  }

  async ensureAuthenticated(): Promise<boolean> {
    await this.initialize();

    if (!this.msal) {
      console.error('[AUTH] ensureAuthenticated failed: MSAL client is not configured');
      return false;
    }

    const account = this.getActiveAccount();
    if (account) {
      return true;
    }

    console.info('[AUTH] No authenticated account during route guard check, redirecting to login');
    await this.msal.loginRedirect(this.getRedirectRequest());
    return false;
  }

  async getToken(): Promise<string | null> {
    if (!this.msal) {
      console.error('[AUTH] getToken skipped: MSAL client is not configured');
      return null;
    }

    const account = this.getActiveAccount();
    if (!account) {
      console.warn('[AUTH] getToken skipped: no active account available');
      return null;
    }

    try {
      console.info('[AUTH] Attempting silent token acquisition', {
        account: account.username,
        scopes: this.scopes,
      });
      const result = await this.msal.acquireTokenSilent(this.getSilentRequest(account));
      return result.accessToken || null;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('[AUTH] Silent token acquisition requires interaction, redirecting', {
          account: account.username,
          scopes: this.scopes,
        });
        await this.msal.acquireTokenRedirect(this.getSilentRequest(account));
        return null;
      }
      console.error('[AUTH] MSAL silent token acquisition failed', error);
      return null;
    }
  }

  private async initializeInternal(): Promise<void> {
    if (!this.msal) {
      console.error('MSAL is not configured. Check env.js Azure settings.');
      return;
    }

    // Step 1: Initialize MSAL
    console.info('[AUTH] Initializing MSAL client');
    await this.msal.initialize();

    // Step 2: Handle redirect from login
    const redirectResult = await this.msal.handleRedirectPromise();
    console.info('[AUTH] MSAL redirect processing complete', {
      hasRedirectResult: !!redirectResult,
    });
    if (redirectResult?.account) {
      console.info('[AUTH] Setting active account from redirect result', {
        account: redirectResult.account.username,
      });
      this.msal.setActiveAccount(redirectResult.account);
    }

    // Step 3: Ensure we have an authenticated account
    const account = this.getActiveAccount();
    if (!account) {
      console.info('[AUTH] No active account, redirecting to MSAL login');
      await this.msal.loginRedirect(this.getRedirectRequest());
      return;
    }

    // Step 4: Get JWT token from MSAL
    const token = await this.getToken();
    if (!token) {
      console.error('[AUTH] Failed to acquire token from MSAL');
      await this.msal.loginRedirect(this.getRedirectRequest());
      return;
    }

    // Step 5: BACKEND-FIRST VALIDATION - Call backend to validate JWT + RBAC
    console.info('[AUTH] Validating token with backend...');
    try {
      await this.validateWithBackend(token, account);
    } catch (error) {
      console.error('[AUTH] Backend validation failed:', error);
      // Redirect to unauthorized page - do NOT allow app to load
      throw error;
    }
  }

  /**
   * NEW: Validate JWT and RBAC with backend BEFORE allowing app to load
   * This is the single source of truth for authentication
   */
  private async validateWithBackend(token: string, account: AccountInfo): Promise<void> {
    try {
      const endpoint = `${this.apiBaseUrl}/auth/me`;
      console.info('[AUTH] Calling backend auth validation endpoint', {
        endpoint,
        account: account.username,
      });
      const response = await fetch(`${this.apiBaseUrl}/auth/me`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        const errorBody = await response.text();
        const statusText = response.statusText || 'Unknown error';
        console.error(
          `[AUTH] Backend validation failed with status ${response.status}`,
          errorBody
        );
        throw new Error(
          `Backend authentication failed: ${response.status} ${statusText}`
        );
      }

      const payload = (await response.json()) as {
        oid?: string;
        preferred_username?: string;
        name?: string;
        roles?: string[];
        highest_role?: string;
      };

      // Validate required fields
      if (!payload.oid || !payload.preferred_username) {
        throw new Error('Backend returned invalid user data (missing oid or preferred_username)');
      }

      const roles = Array.isArray(payload.roles) ? payload.roles : [];
      const highestRole = typeof payload.highest_role === 'string' ? payload.highest_role : '';

      console.info('[AUTH] Backend validation successful', {
        user: payload.preferred_username,
        roles,
        highestRole,
      });

      // Set roles based on backend response
      this.roleService.setUserRoles(roles, highestRole);
    } catch (error) {
      // If backend validation fails, app should NOT load
      // The caller (APP_INITIALIZER) will handle the error and show unauthorized page
      throw new Error(`[AUTH] Backend validation failed: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Get the currently authenticated user account information
   * Returns null if no user is authenticated
   */
  getUserAccount(): AccountInfo | null {
    return this.getActiveAccount();
  }

  /**
   * Sign out the user and clear authentication state
   */
  async logout(): Promise<void> {
    if (!this.msal) {
      console.error('[AUTH] logout failed: MSAL client is not configured');
      return;
    }

    try {
      console.info('[AUTH] Logging out user');
      await this.msal.logoutRedirect();
    } catch (error) {
      console.error('[AUTH] Logout failed', error);
      // Still redirect even if logout fails
      window.location.href = window.location.origin;
    }
  }

  private getActiveAccount(): AccountInfo | null {
    if (!this.msal) {
      return null;
    }

    const active = this.msal.getActiveAccount();
    if (active) {
      return active;
    }

    const accounts = this.msal.getAllAccounts();
    if (accounts.length === 0) {
      console.warn('[AUTH] No MSAL accounts found in cache');
      return null;
    }

    console.info('[AUTH] Promoting first cached MSAL account to active account', {
      account: accounts[0].username,
      cachedAccountCount: accounts.length,
    });
    this.msal.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  private getRedirectRequest(): RedirectRequest {
    return {
      scopes: this.scopes,
    };
  }

  private getSilentRequest(account: AccountInfo): SilentRequest {
    return {
      account,
      scopes: this.scopes,
    };
  }

  private getEnv(): FrontendEnv {
    const raw = (window as unknown as { __env__?: FrontendEnv }).__env__;
    return raw || {};
  }

  private createMsalClient(): PublicClientApplication | null {
    const clientId = this.frontendClientId;
    const tenantId = this.tenantId;

    if (!clientId || !tenantId) {
      console.error('[AUTH] Missing MSAL configuration values', {
        hasClientId: !!clientId,
        hasTenantId: !!tenantId,
      });
      return null;
    }

    const authority = `https://login.microsoftonline.com/${tenantId}`;

    console.info('[AUTH] Creating MSAL client', {
      authority,
      redirectUri: window.location.origin,
      cacheLocation: 'sessionStorage',
    });

    return new PublicClientApplication({
      auth: {
        clientId,
        authority,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: 'sessionStorage',
      },
    });
  }
}
